import { useCallback, useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";

// Icons
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";

// Contexts
import LoaderContext from "../../../../context/psikotest/LoaderContext";
import SnackbarContext from "../../../../context/psikotest/SnackbarContext";

// Helpers
import errorHandler from "../../../../helper/psikotest/errorHandler";

// Controller
import AssessmentPersonalitySubtest from "../../../../controller/psikotest/psikotest_system/assessment_personality_subtest"
import AssessmentParticipantPersonalitySession from "../../../../controller/psikotest/psikotest_system/assessment_participant_personality_session";
import Assessment from "../../../../controller/psikotest/psikotest_system/assessment";
import {
    IntegritySummaryStrip,
    ParticipantIntegrityState,
    lookupLiveRow,
    useIntegrityLive,
} from "../../../../components/shared/sessionIntegrity/admin";

function ModalPersonalitySubtestSession(
    props: {
        data?: TPSAssessmentPersonalitySubtest;
        assessment_id: number;
        handleClose: () => void;
        assessment_personality_subtest: TPSAssessmentPersonalitySubtest[];
    }) {
    // Contexts
    const { setMessage } = useContext(LoaderContext);
    const { setNotif } = useContext(SnackbarContext);
    // state
    const [personality_subtest_session, setPersonalitySubtestSession] = useState<TPSAssessmentPersonalitySubtestParticipantSession[]>([])
    const loadIntegrity = useCallback((assessmentId: string) => {
        const controller = new Assessment();
        return controller.integrity_live(assessmentId);
    }, []);
    const integrity = useIntegrityLive(props.assessment_id, loadIntegrity);

    const getDoc = useCallback(
        async function getDoc() {
            try {
                // fetch personality subtest
                setMessage("Fetch Subtest Participant Session");
                if (props.data) {
                    const caps = new AssessmentPersonalitySubtest()
                    const res = await caps.participant_session(props.data)
                    setPersonalitySubtestSession(res.data.list)
                }
                setMessage("");
            } catch (error) {
                setMessage("");
                const errorMessage = errorHandler(error);
                setNotif({ type: "error", message: errorMessage });
            }
        },
        [setNotif, setMessage, props.data]
    );

    useEffect(() => {
        getDoc();
    }, [getDoc]);

    const resetPersonalitySession = async (acsps: TPSAssessmentPersonalitySubtestParticipantSession) => {
        try {
            const confirm = await Swal.fire({
                title: "Atur ulang sesi?",
                text: `Sesi ${props.data?.personality_subtest_name} milik ${acsps.participant.full_name} akan diatur ulang.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Ya, atur ulang",
                cancelButtonText: "Batal",
            });
            if (confirm.isConfirmed) {
                if (acsps.personality_session.personality_subtest_name) {
                    // reset ist subtest
                    setMessage("Reset Participant Personality Session");
                    const capcs = new AssessmentParticipantPersonalitySession()
                    const res = await capcs.reset(acsps.personality_session.id, acsps.personality_session.personality_subtest_name)
                    // re-fetch
                    getDoc()
                    void integrity.refresh()
                    setNotif({ type: "success", message: res.data.message })
                    setMessage("");
                }
            }
        } catch (error) {
            setMessage("");
            const errorMessage = errorHandler(error);
            setNotif({ type: "error", message: errorMessage });
        }
    }

    return <div>
        <div className="fixed z-20 top-0 left-0 w-screen h-screen bg-black opacity-20"></div>
        <div className="fixed z-30 top-0 left-0 w-screen h-screen bg-transparent flex items-center justify-center">
            <div className="flex flex-col bg-white w-screen md:w-[90%] h-[90%] md:rounded">
                <div className="p-4 bg-green-800 md:rounded-t text-white flex justify-between">
                    <h3>{props.data?.personality_subtest_name} - Sesi subtes</h3>
                    <XMarkIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent cursor-pointer" aria-hidden="true" onClick={() => props.handleClose()} />
                </div>
                <div className="px-2 py-10 overflow-auto">
                    <IntegritySummaryStrip summary={integrity.summary} available={integrity.available} />
                    <table className="w-full border">
                        <thead className="bg-slate-600 text-white">
                            <tr>
                                <th className="py-1 px-2 w-8 border border-slate-500">No</th>
                                <th className="py-1 px-2 text-left border border-slate-500">Peserta</th>
                                <th className="py-1 px-2 w-20 border border-slate-500">Status</th>
                                <th className="py-1 px-2 text-left border border-slate-500">Integritas sesi</th>
                                <th className="py-1 px-2 w-20 border border-slate-500">Atur ulang</th>
                            </tr>
                        </thead>
                        <tbody>
                            {personality_subtest_session.map((css, i) => {
                                return (
                                    <tr key={`personality_subtest_session_${i}`}>
                                        <td className="py-1 px-2 text-center border">{i + 1}</td>
                                        <td className="py-1 px-2 border">{css.participant.full_name}</td>
                                        <td className="py-1 px-2 border whitespace-nowrap">
                                            {css.personality_session.start_time === null && css.personality_session.close_by_user === null
                                                ? <div className="flex text-red-500"><ExclamationCircleIcon className="w-5 mr-1" /> Belum dimulai</div>
                                                : css.remaining_time > 0
                                                    ? <div className="flex text-black-500"><ClockIcon className="w-5 mr-1" /> Sedang berjalan</div>
                                                    : <div className="flex text-green-500"><CheckCircleIcon className="w-5 mr-1" /> Selesai</div>
                                            }
                                        </td>
                                        <td className="py-1 px-2 border">
                                            <ParticipantIntegrityState row={lookupLiveRow(integrity.bySession, css.participant.id, String(props.data?.personality_subtest_name), css.personality_session.id)} />
                                        </td>
                                        <td className="py-1 px-2 text-center border">
                                            <button
                                                type="button"
                                                className="px-2 rounded bg-red-500 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                                                onClick={() => resetPersonalitySession(css)}
                                                disabled={css.personality_session.start_time === null && css.personality_session.close_by_user === null}
                                            >
                                                Atur ulang
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
}

export default ModalPersonalitySubtestSession;